import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom'; // <-- add useLocation
import { BASE_URL } from '../apiConfig';
import Navbar from './navbar';
import Footer from './Footer';
import RippleWaveLoader from "./ui/RippleWaveLoader";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { jsPDF } from 'jspdf';

interface BasicInfo {
  company_name: string;
  website: string;
  basic_info: {
    founded_year: string;
    headquarters: string;
    founders: string[];
    team_size: string;
    sector: string;
  };
  technology_stack: {
    languages: string[];
    frameworks: string[];
    cloud_providers: string[];
    core_technologies: string[];
  };
  strategy: {
    market_focus: string;
    differentiators: string;
    partners: string[];
    competitive_advantage: string;
  };
}

interface InsightInfo {
  company_name: string;
  financials: {
    revenue: string;
    valuation: string;
    funding_rounds: Array<{
      amount: string;
      date: string;
      year?: string;
      description: string;
      lead_investors?: string[];
    }>;
    growth_rate: string;
    profitability: string;
  };
  market: {
    industry: string;
    market_share: string;
    competitors: string[];
    opportunities: string;
    risks: string;
  };
  sentiment: {
    overall: string;
    topics: string[];
    headlines: Array<{
      title: string;
      summary: string;
      source: string;
    }>;
  };
}

interface ScoringResults {
  company_name: string;
  scores: {
    strategic_alignment: number;
    technology_strength: number;
    talent_and_leadership: number;
    market_potential: number;
    financials: number;
    scalability: number;
    social_impact: number;
    compliance_and_risk: number;
    cultural_fit: number;
    valuation_and_terms: number;
    tech_interoperability: number;
  };
  weighted_total: number;
  qualitative_summary: string;
}

const CompanyDetailPage = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const location = useLocation();
  const [basicInfo] = useState<BasicInfo | null>(
    location.state?.basicInfo || null // <-- get from location.state
  );
  const [insightInfo, setInsightInfo] = useState<InsightInfo | null>(null);
  const [scoringResults, setScoringResults] = useState<ScoringResults | null>(null);
  const [scoringSources, setScoringSources] = useState<string[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  // Removed unused loadingProgress state
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Generate PDF from available data
  const handleDownloadPDF = async () => {
    if (!basicInfo) return;
    setIsDownloadingPdf(true);

    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = 210, pageHeight = 297, margin = 15;
      const usableWidth = pageWidth - margin * 2;
      const lineH = 6;
      let y = margin;

      const checkPage = (extra = lineH) => {
        if (y + extra > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
      };

      const text = (t: string, size = 10, style: 'normal' | 'bold' = 'normal', color: [number,number,number] = [20,20,20]) => {
        pdf.setFont('helvetica', style as any);
        pdf.setFontSize(size);
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(t || '-', usableWidth);
        lines.forEach((ln: string) => {
          checkPage();
          pdf.text(ln, margin, y);
          y += lineH;
        });
      };

      const header = (t: string) => {
        checkPage(lineH * 2);
        y += 2;
        text(t, 13, 'bold', [255, 84, 31]);
        y += 2;
      };

      const kv = (k: string, v: string) => text(`${k}: ${v || '-'}`, 10, 'normal', [40,40,40]);

      const chips = (k: string, arr?: string[]) => {
        if (!arr || arr.length === 0) return;
        kv(k, arr.join(', '));
      };

      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text(basicInfo.company_name || 'Company', pageWidth / 2, y, { align: 'center' });
      y += 8;

      // Basic Info
      header('Basic Information');
      kv('Website', basicInfo.website || '');
      kv('Founded', basicInfo.basic_info?.founded_year || '');
      kv('Headquarters', basicInfo.basic_info?.headquarters || '');
      kv('Team Size', basicInfo.basic_info?.team_size || '');
      kv('Sector', basicInfo.basic_info?.sector || '');
      chips('Founders', basicInfo.basic_info?.founders);

      // Technology
      header('Technology Stack');
      chips('Languages', basicInfo.technology_stack?.languages);
      chips('Frameworks', basicInfo.technology_stack?.frameworks);
      chips('Cloud Providers', basicInfo.technology_stack?.cloud_providers);
      chips('Core Technologies', basicInfo.technology_stack?.core_technologies);

      // Strategy
      header('Strategy');
      kv('Market Focus', basicInfo.strategy?.market_focus || '');
      kv('Differentiators', basicInfo.strategy?.differentiators || '');
      chips('Partners', basicInfo.strategy?.partners);
      kv('Competitive Advantage', basicInfo.strategy?.competitive_advantage || '');

      // Insights
      if (insightInfo) {
        header('Financials');
        kv('Revenue', insightInfo.financials?.revenue || '');
        kv('Valuation', insightInfo.financials?.valuation || '');
        kv('Growth Rate', insightInfo.financials?.growth_rate || '');
        kv('Profitability', insightInfo.financials?.profitability || '');
        if (Array.isArray(insightInfo.financials?.funding_rounds) && insightInfo.financials.funding_rounds.length) {
          text('Funding Rounds:', 11, 'bold', [40,40,40]);
          insightInfo.financials.funding_rounds.forEach((r) => {
            const round = [
              r.amount || '',
              r.date || r['year'] || '',
              r.description || '',
              r.lead_investors && r.lead_investors.length ? `Lead: ${r.lead_investors.join(', ')}` : ''
            ].filter(Boolean).join(' • ');
            text(`- ${round}`, 10, 'normal', [55,55,55]);
          });
        }

        header('Market');
        kv('Industry', insightInfo.market?.industry || '');
        kv('Market Share', insightInfo.market?.market_share || '');
        kv('Opportunities', insightInfo.market?.opportunities || '');
        kv('Risks', insightInfo.market?.risks || '');
        chips('Competitors', insightInfo.market?.competitors);

        if (insightInfo.sentiment?.headlines?.length) {
          header('Recent Headlines');
          insightInfo.sentiment.headlines.forEach((h) => {
            text(`• ${h.title} (${h.source})`, 10, 'bold', [60,60,60]);
            if (h.summary) text(h.summary, 10, 'normal', [80,80,80]);
          });
        }
      }

      // Scores
      if (scoringResults) {
        header('Scores');
        Object.entries(scoringResults.scores || {}).forEach(([k, v]) => {
          kv(k.replace(/_/g, ' '), `${v}%`);
        });
        kv('Overall', `${Math.round(scoringResults.weighted_total ?? 0)}%`);
        if (scoringResults.qualitative_summary) {
          header('Summary');
          text(scoringResults.qualitative_summary, 10);
        }
      }

      // Save
      const fname = `${(basicInfo.company_name || 'company').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_${new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(fname);
    } catch (e) {
      console.error('PDF error:', e);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (!companyName || !basicInfo) return;

      setIsLoading(true);
      // Removed setLoadingProgress(0);

      try {
        // Removed simulated progress interval and loadingProgress updates

        const response = await fetch(`${BASE_URL}/score_acquisition_company`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_name: companyName,
            basic_info: basicInfo,
          }),
        });

        // Removed clearInterval(progressInterval) and setLoadingProgress(100);

        const data = await response.json();

        if (data.status === 'success') {
          // Do NOT update basicInfo here!
          // setBasicInfo(data.basic_info); // <-- REMOVE THIS LINE
          setInsightInfo(data.insight_info);
          setScoringResults(data.scoring_results);
          setScoringSources(data.sources || []);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setTimeout(() => setIsLoading(false), 500);
      }
    };

    fetchCompanyDetails();
  }, [companyName]); // <-- Only depend on companyName

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20">
          <div className="flex flex-col items-center justify-center gap-8">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-semibold mb-2">Analyzing {companyName}</h2>
            </div>
            <RippleWaveLoader messages={[
              'Searching web for company data…',
              'Scoring market position and financials…',
              'Evaluating technology stack…',
              'Computing acquisition fit score…',
              'Almost done…',
            ]} />
          </div>
        </div>
      </div>
    );
  }



  return (
    <div
      className="min-h-screen relative flex flex-col overflow-x-hidden"
      style={{
        background: "radial-gradient(35% 8% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      <Navbar />

      {/* Decorative background elements */}
      <div className="absolute bg-dots" style={{
        width:"1106px",height:"500px",top:"-12px",left:"194px",
        backgroundImage:"url('/dots.png')",backgroundSize:"contain",
        backgroundRepeat:"no-repeat",pointerEvents:"none",zIndex:0
      }}/>

      <div className="max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 pt-32 pb-20 relative z-10">
        {/* Header Section - Single Row Layout */}
        <div className="mb-10">
          {/* Desktop Layout - Single Row */}
          <div className="hidden md:flex items-center justify-between gap-6 mb-6">
            {/* Left: Back Button */}
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium font-[Sora] text-white/90 bg-white/5 hover:bg-white/10 rounded-full border border-white/10 transition-all duration-200 shrink-0"
            >
              <ArrowBackIcon className="w-4 h-4" />
              Back
            </button>

            {/* Center: Company Name */}
            <h1
              className="text-2xl lg:text-3xl font-bold font-[Sora] bg-gradient-to-r from-white to-neutral-300 bg-clip-text text-transparent text-center flex-1 truncate px-4"
              title={basicInfo?.company_name}
            >
              {basicInfo?.company_name}
            </h1>

            {/* Right: Download PDF Button */}
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloadingPdf}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold font-[Sora] bg-white/5 hover:bg-white/10 rounded-full border border-white/15 disabled:opacity-60 transition-all duration-200 shrink-0"
              aria-label="Download PDF"
            >
              <DownloadIcon className="w-4 h-4" />
              {isDownloadingPdf ? 'Preparing...' : 'Download PDF'}
            </button>
          </div>

          {/* Mobile Layout - Stacked */}
          <div className="md:hidden space-y-4">
            {/* Back button */}
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium font-[Sora] text-white/90 bg-white/5 hover:bg-white/10 rounded-full border border-white/10 transition-all duration-200"
            >
              <ArrowBackIcon className="w-4 h-4" />
              Back
            </button>

            {/* Company Name */}
            <h1
              className="text-3xl font-bold font-[Sora] bg-gradient-to-r from-white to-neutral-300 bg-clip-text text-transparent"
            >
              {basicInfo?.company_name}
            </h1>

            {/* Download PDF Button */}
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloadingPdf}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold font-[Sora] bg-white/5 hover:bg-white/10 rounded-full border border-white/15 disabled:opacity-60 transition-all duration-200"
              aria-label="Download PDF"
            >
              <DownloadIcon className="w-4 h-4" />
              {isDownloadingPdf ? 'Preparing...' : 'Download PDF'}
            </button>
          </div>
        </div>

        {/* Top cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Company Overview */}
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/10 hover:-translate-y-1 cursor-pointer"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Company Overview</h2>
            <div className="space-y-4">
              <InfoItem label="Website" value={basicInfo?.website || ""} isLink />
              <InfoItem label="Founded" value={basicInfo?.basic_info.founded_year || ""} />
              <InfoItem label="Headquarters" value={basicInfo?.basic_info.headquarters || ""} />
              <InfoItem label="Team Size" value={basicInfo?.basic_info.team_size || ""} />
              <InfoItem label="Sector" value={basicInfo?.basic_info.sector || ""} />

              {basicInfo?.basic_info.founders?.length ? (
                <ExpandableSection title="Founders" defaultExpanded={false}>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {basicInfo.basic_info.founders.map((f) => (
                      <span key={f} className="px-4 py-2 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-full text-sm font-[Sora] text-white hover:border-orange-500/40 transition-all duration-200">{f}</span>
                    ))}
                  </div>
                </ExpandableSection>
              ) : null}
            </div>
          </div>

          {/* Technology Stack */}
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-1 cursor-pointer"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Technology Stack</h2>
            {basicInfo?.technology_stack && (
              <div className="space-y-5">
                {basicInfo.technology_stack.languages?.length ? (
                  <div>
                    <span className="text-sm font-[Sora] text-neutral-400 mb-2 block">Programming Languages</span>
                    <div className="flex flex-wrap gap-2">
                      {basicInfo.technology_stack.languages.map((lang) => (
                        <span key={lang} className="px-3 py-1.5 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-full text-sm font-[Sora] text-orange-200 hover:border-orange-500/40 transition-all duration-200">{lang}</span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {basicInfo.technology_stack.frameworks?.length ? (
                  <div>
                    <span className="text-sm font-[Sora] text-neutral-400 mb-2 block">Frameworks</span>
                    <div className="flex flex-wrap gap-2">
                      {basicInfo.technology_stack.frameworks.map((fw) => (
                        <span key={fw} className="px-3 py-1.5 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-full text-sm font-[Sora] text-blue-200 hover:border-blue-500/40 transition-all duration-200">{fw}</span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {basicInfo.technology_stack.cloud_providers?.length ? (
                  <div>
                    <span className="text-sm font-[Sora] text-neutral-400 mb-2 block">Cloud Providers</span>
                    <div className="flex flex-wrap gap-2">
                      {basicInfo.technology_stack.cloud_providers.map((cp) => (
                        <span key={cp} className="px-3 py-1.5 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-full text-sm font-[Sora] text-purple-200 hover:border-purple-500/40 transition-all duration-200">{cp}</span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {basicInfo.technology_stack.core_technologies?.length ? (
                  <ExpandableSection title="Core Technologies">
                    <div className="flex flex-wrap gap-2 mt-2">
                      {basicInfo.technology_stack.core_technologies.map((t) => (
                        <span key={t} className="px-3 py-1.5 bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-full text-sm font-[Sora] text-green-200 hover:border-green-500/40 transition-all duration-200">{t}</span>
                      ))}
                    </div>
                  </ExpandableSection>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Strategy card */}
        <div
          className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/10 hover:-translate-y-1 cursor-pointer mb-8"
          style={{
            background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
          }}
        >
          <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Strategy</h2>
          <div className="space-y-4">
            <ExpandableText label="Market Focus" text={basicInfo?.strategy.market_focus || ""} />
            <ExpandableText label="Differentiators" text={basicInfo?.strategy.differentiators || ""} />
            {basicInfo?.strategy.partners?.length ? (
              <div className="pb-4 border-b border-white/5 last:border-b-0">
                <div className="text-sm font-[Sora] text-neutral-400 mb-3">Partners</div>
                <div className="flex flex-wrap gap-2">
                  {basicInfo.strategy.partners.map((p) => (
                    <span key={p} className="px-4 py-2 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 rounded-full text-sm font-[Sora] text-indigo-200 hover:border-indigo-500/40 transition-all duration-200">{p}</span>
                  ))}
                </div>
              </div>
            ) : null}
            <ExpandableText label="Competitive Advantage" text={basicInfo?.strategy.competitive_advantage || ""} />
          </div>
        </div>

        {/* Financial + Market */}
        {insightInfo && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div
              className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-green-500/10 hover:-translate-y-1 cursor-pointer"
              style={{
                background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
              }}
            >
              <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Financial Overview</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <InfoItem label="Revenue" value={insightInfo?.financials.revenue || ""} />
                  <InfoItem label="Valuation" value={insightInfo?.financials.valuation || ""} />
                  <InfoItem label="Growth Rate" value={insightInfo?.financials.growth_rate || ""} />
                  <InfoItem label="Profitability" value={insightInfo?.financials.profitability || ""} />
                </div>
                {insightInfo.financials.funding_rounds?.length ? (
                  <ExpandableSection title="Funding History">
                    <div className="space-y-4 mt-4">
                      {insightInfo.financials.funding_rounds.map((round, idx) => (
                        <div key={idx} className="border-l-4 border-orange-500 pl-5 py-3 bg-gradient-to-r from-orange-500/5 to-transparent rounded-r-xl hover:from-orange-500/10 transition-all duration-200">
                          <div className="flex items-baseline gap-2 mb-1">
                            <div className="text-xl font-bold font-[Sora] text-white">{round.amount}</div>
                            <div className="text-sm text-neutral-400 font-[Sora]">{round.date || (round as any).year || ""}</div>
                          </div>
                          {round.description ? (
                            <div className="text-sm font-[Sora] text-neutral-300 mt-2 leading-relaxed">{round.description}</div>
                          ) : null}
                          {round.lead_investors?.length ? (
                            <div className="mt-3">
                              <span className="text-xs text-neutral-400 font-[Sora] block mb-2">Lead Investors:</span>
                              <div className="flex flex-wrap gap-2">
                                {round.lead_investors.map((i) => (
                                  <span key={i} className="px-3 py-1 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-full text-xs font-[Sora] text-blue-200">{i}</span>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </ExpandableSection>
                ) : null}
              </div>
            </div>

            <div
              className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 cursor-pointer"
              style={{
                background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
              }}
            >
              <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Market Position</h2>
              <div className="space-y-4">
                <InfoItem label="Industry" value={insightInfo?.market.industry || ""} />
                <InfoItem label="Market Share" value={insightInfo?.market.market_share || ""} />
                <ExpandableText label="Opportunities" text={insightInfo?.market.opportunities || ""} />
                <ExpandableSection title="Competition & Risks">
                  <div className="space-y-4">
                    {insightInfo.market.competitors?.length ? (
                      <div>
                        <span className="text-sm text-neutral-400 font-[Sora] block mb-3">Key Competitors</span>
                        <div className="flex flex-wrap gap-2">
                          {insightInfo.market.competitors.map((c) => (
                            <span key={c} className="px-4 py-2 bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20 rounded-full text-sm font-[Sora] text-red-200 hover:border-red-500/40 transition-all duration-200">{c}</span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <ExpandableText label="Risks" text={insightInfo?.market.risks || ""} />
                  </div>
                </ExpandableSection>

                {insightInfo.sentiment?.headlines?.length ? (
                  <ExpandableSection title="Recent Headlines">
                    <div className="space-y-4 mt-4">
                      {insightInfo.sentiment.headlines.map((h, idx) => (
                        <div key={idx} className="border-l-4 border-blue-500 pl-5 py-3 bg-gradient-to-r from-blue-500/5 to-transparent rounded-r-xl hover:from-blue-500/10 transition-all duration-200">
                          <div className="font-semibold font-[Sora] text-white text-base mb-2">{h.title}</div>
                          <div className="text-sm font-[Sora] text-neutral-300 leading-relaxed mb-2">{h.summary}</div>
                          <div className="text-xs font-[Sora] text-neutral-500 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                            Source: {h.source}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ExpandableSection>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Scores */}
        {scoringResults && (
          <div className="mb-12">
            <h2 className="text-3xl font-semibold font-[Sora] mb-8 text-white">Analysis Scores</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/10 hover:-translate-y-1 cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
                }}
              >
                <h3 className="text-xl font-semibold font-[Sora] mb-6 text-white">Score Breakdown</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(scoringResults.scores).map(([k, v]) => {
                    const pct = Math.min(100, Math.max(0, Number(v)));
                    const label = pct >= 80 ? 'Strong' : pct >= 65 ? 'Good' : pct >= 50 ? 'Fair' : 'Weak';
                    const labelColor = pct >= 80 ? 'text-green-400' : pct >= 65 ? 'text-yellow-400' : pct >= 50 ? 'text-orange-400' : 'text-red-400';
                    const barColor = pct >= 80 ? 'from-green-500 to-green-400' : pct >= 65 ? 'from-yellow-500 to-yellow-400' : pct >= 50 ? 'from-orange-500 to-orange-400' : 'from-red-500 to-red-400';
                    return (
                      <div key={k} className="flex flex-col bg-gradient-to-br from-white/5 to-white/[0.02] rounded-xl p-5 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-neutral-400 text-xs capitalize font-[Sora] group-hover:text-neutral-300 transition-colors">{k.replace(/_/g, ' ')}</span>
                          <span className={`text-xs font-medium font-[Sora] ${labelColor}`}>{label}</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-white text-2xl font-bold font-[Sora]">{pct}</span>
                          <span className="text-neutral-500 text-sm font-[Sora]">%</span>
                        </div>
                        {/* Score bar */}
                        <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${barColor} rounded-full transition-all duration-500`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div
                className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/10 hover:-translate-y-1 cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
                }}
              >
                <h3 className="text-xl font-semibold font-[Sora] mb-6 text-white">Qualitative Summary</h3>
                <p className="text-neutral-300 font-[Sora] leading-[1.8] mb-8">{scoringResults.qualitative_summary}</p>

                {/* Overall Score Card */}
                {(() => {
                  const total = Math.min(100, Math.max(0, scoringResults.weighted_total));
                  const overallLabel = total >= 80 ? 'Strong Acquisition Target' : total >= 65 ? 'Good Candidate' : total >= 50 ? 'Fair Candidate' : 'Weak Candidate';
                  return (
                    <div className="mt-6 p-6 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/30 rounded-xl">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-neutral-300 font-[Sora] text-sm">Overall Score</span>
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold font-[Sora] bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">
                            {Math.round(total)}
                          </span>
                          <span className="text-neutral-400 text-lg font-[Sora]">%</span>
                        </div>
                      </div>
                      <p className="text-xs text-orange-300/70 font-[Sora] mb-3">{overallLabel}</p>
                      {/* Overall score bar */}
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-500 via-orange-400 to-orange-300 rounded-full transition-all duration-500 shadow-lg shadow-orange-500/50"
                          style={{ width: `${total}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Sources */}
            {scoringSources.length > 0 && (
              <div className="mt-6">
                <p className="text-xs text-neutral-500 font-[Sora] mb-3 uppercase tracking-wide">Analysis based on</p>
                <div className="flex flex-wrap gap-2">
                  {scoringSources.map((src, i) => {
                    let domain = src;
                    try { domain = new URL(src).hostname.replace('www.', ''); } catch {}
                    return (
                      <a
                        key={i}
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/30 text-xs text-neutral-400 hover:text-white transition-all"
                      >
                        <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" className="w-3.5 h-3.5" />
                        {domain}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Helper component for displaying info items
interface InfoItemProps {
  label: string;
  value: string;
  isLink?: boolean;
}

const InfoItem = ({ label, value, isLink = false }: InfoItemProps) => {
  if (!value) return null;

  return (
    <div className="pb-4 border-b border-white/5 last:border-b-0">
      <div className="text-sm font-[Sora] text-neutral-400 mb-2">{label}</div>
      {isLink && value.startsWith('http') ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-orange-400 hover:text-orange-300 font-[Sora] transition-colors duration-200 inline-flex items-center gap-2"
        >
          {value}
          <OpenInNewIcon className="w-4 h-4" />
        </a>
      ) : (
        <div className="text-neutral-200 font-[Sora]">{value}</div>
      )}
    </div>
  );
};

// First, add this new interface for expandable sections
interface ExpandableSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({
  title,
  children,
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border-b border-white/5 last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full py-4 flex items-center justify-between text-left group hover:bg-white/5 rounded-lg px-2 transition-all duration-200"
      >
        <span className="text-lg font-semibold font-[Sora] text-white group-hover:text-orange-400 transition-colors">{title}</span>
        <span className="text-2xl text-orange-400 font-light transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)' }}>
          +
        </span>
      </button>
      {isExpanded && <div className="pb-4">{children}</div>}
    </div>
  );
};

// Add a small ExpandableText helper for long fields
interface ExpandableTextProps { label: string; text: string; threshold?: number; }
const ExpandableText = ({ label, text, threshold = 160 }: ExpandableTextProps) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const isLong = text.length > threshold;
  const shown = isLong && !open ? text.slice(0, threshold) + '…' : text;
  return (
    <div className="pb-4 border-b border-white/5 last:border-b-0">
      <div className="text-sm font-[Sora] text-neutral-400 mb-2">{label}</div>
      <div className="text-neutral-200 font-[Sora] leading-relaxed">
        {shown}
        {isLong && (
          <button
            className="ml-2 text-orange-400 hover:text-orange-300 font-[Sora] text-sm font-medium transition-colors duration-200"
            onClick={() => setOpen(!open)}
          >
            {open ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanyDetailPage;